"use client";

import { useState, useRef, useEffect, useCallback } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { Search, X, Loader2, Clock } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useTranslation } from "@/hooks/useTranslation";

// Placeholder rotates through these; content lives in i18n.ts.
const SEARCH_HINT_KEYS = ["search.placeholder", "search.hint2", "search.hint3"] as const;

const HISTORY_KEY = "searchHistory";
const MAX_HISTORY = 10;

function getHistory(): string[] {
  if (typeof window === "undefined") return [];
  try {
    return JSON.parse(localStorage.getItem(HISTORY_KEY) || "[]");
  } catch {
    return [];
  }
}

function saveToHistory(query: string) {
  const history = getHistory().filter((h) => h !== query);
  history.unshift(query);
  localStorage.setItem(HISTORY_KEY, JSON.stringify(history.slice(0, MAX_HISTORY)));
}

function removeFromHistory(query: string) {
  const history = getHistory().filter((h) => h !== query);
  localStorage.setItem(HISTORY_KEY, JSON.stringify(history));
}

function clearHistory() {
  localStorage.removeItem(HISTORY_KEY);
}

interface SearchBarProps {
  autoFocus?: boolean;
  onSearch?: (query: string) => void;
}

export function SearchBar({
  autoFocus = false,
  onSearch,
}: SearchBarProps) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();

  const [query, setQuery] = useState(searchParams.get("q") ?? "");
  const [isLoading, setIsLoading] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState<string[]>([]);

  const [hintIndex, setHintIndex] = useState(0);

  useEffect(() => {
    setHintIndex(0);
    const interval = setInterval(() => {
      setHintIndex((i) => (i + 1) % SEARCH_HINT_KEYS.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  const placeholder = t(SEARCH_HINT_KEYS[hintIndex]);
  const defaultQuery = placeholder;

  useEffect(() => {
    if (autoFocus && inputRef.current) {
      inputRef.current.focus();
    }
  }, [autoFocus]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node) &&
          inputRef.current && !inputRef.current.contains(e.target as Node)) {
        setShowHistory(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const doSearch = useCallback((searchQuery: string) => {
    const trimmed = searchQuery.trim() || defaultQuery;
    saveToHistory(trimmed);
    setQuery(trimmed);
    setShowHistory(false);
    setIsLoading(true);

    if (onSearch) {
      onSearch(trimmed);
    } else {
      router.push(`/search?q=${encodeURIComponent(trimmed)}`);
    }

    setIsLoading(false);
  }, [defaultQuery, onSearch, router]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    doSearch(query);
  };

  const handleFocus = () => {
    if (query) return;
    const h = getHistory();
    setHistory(h);
    if (h.length > 0) setShowHistory(true);
  };

  const handleClear = () => {
    setQuery("");
    const h = getHistory();
    setHistory(h);
    if (h.length > 0) setShowHistory(true);
    inputRef.current?.focus();
  };

  const handleRemoveHistory = (item: string, e: React.MouseEvent) => {
    e.stopPropagation();
    removeFromHistory(item);
    setHistory((prev) => prev.filter((h) => h !== item));
  };

  const handleClearHistory = () => {
    clearHistory();
    setHistory([]);
    setShowHistory(false);
  };

  const historyLabel = t("search.history");
  const clearLabel = t("search.clearHistory");

  return (
    <form onSubmit={handleSubmit} className="relative w-full max-w-2xl" data-tour="search">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            if (e.target.value) setShowHistory(false);
          }}
          onFocus={handleFocus}
          placeholder={placeholder}
          className="h-12 pl-10 pr-20 text-base"
          disabled={isLoading}
        />
        <div className="absolute right-2 top-1/2 flex -translate-y-1/2 items-center gap-1">
          {query && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={handleClear}
              className="h-8 w-8"
            >
              <X className="h-4 w-4" />
            </Button>
          )}
          <Button type="submit" size="sm" disabled={isLoading}>
            {isLoading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              t("search.button")
            )}
          </Button>
        </div>
      </div>

      {/* Search history dropdown */}
      {showHistory && history.length > 0 && (
        <div
          ref={dropdownRef}
          className="absolute left-0 right-0 top-full z-20 mt-1 overflow-hidden rounded-lg border border-border bg-background shadow-lg"
        >
          <div className="flex items-center justify-between px-3 py-2">
            <span className="text-xs font-medium text-muted-foreground">{historyLabel}</span>
            <button
              type="button"
              onClick={handleClearHistory}
              className="text-xs text-muted-foreground hover:text-foreground"
            >
              {clearLabel}
            </button>
          </div>
          {history.map((item) => (
            <button
              key={item}
              type="button"
              onClick={() => doSearch(item)}
              className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition-colors hover:bg-muted"
            >
              <Clock className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
              <span className="flex-1 truncate">{item}</span>
              <span
                onClick={(e) => handleRemoveHistory(item, e)}
                className="shrink-0 text-muted-foreground hover:text-foreground"
              >
                <X className="h-3.5 w-3.5" />
              </span>
            </button>
          ))}
        </div>
      )}
    </form>
  );
}
